import { useState, useEffect, useRef } from 'react';
import api from '../api';
import toast from 'react-hot-toast';

const EMPTY_FORM = { name: '', ip_address: '', username: '', password: '', client_id: '', location: '', camera_type: 'both', api_endpoint: '', lat: '', lng: '', is_active: 1, scenarios: '[2]' };


// Žemėlapio koordinačių pasirinkimo komponentas
function MapPicker({ lat, lng, onPick }) {
  const mapRef  = useRef(null);
  const lmap    = useRef(null);
  const marker  = useRef(null);

  useEffect(() => {
    function initMap() {
      if (!window.L || !mapRef.current || lmap.current) return;
      const L = window.L;
      const center = lat && lng ? [parseFloat(lat), parseFloat(lng)] : [54.6872, 25.2797];
      lmap.current = L.map(mapRef.current).setView(center, 15);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { maxZoom:19 }).addTo(lmap.current);

      if (lat && lng) {
        marker.current = L.marker([parseFloat(lat), parseFloat(lng)]).addTo(lmap.current);
      }

      lmap.current.on('click', e => {
        const { lat: la, lng: lo } = e.latlng;
        if (marker.current) marker.current.setLatLng([la, lo]);
        else { marker.current = L.marker([la, lo]).addTo(lmap.current); }
        onPick(la.toFixed(6), lo.toFixed(6));
      });
    }

    if (window.L) { initMap(); return; }
    if (!document.querySelector('link[href*="leaflet"]')) {
      const link = document.createElement('link');
      link.rel = 'stylesheet'; link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
    }
    if (!document.querySelector('script[src*="leaflet"]')) {
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      script.onload = initMap;
      document.head.appendChild(script);
    } else {
      const interval = setInterval(() => { if (window.L) { clearInterval(interval); initMap(); } }, 100);
    }
    return () => { if (lmap.current) { lmap.current.remove(); lmap.current = null; marker.current = null; } };
  }, []);

  // Update marker if coords change externally
  useEffect(() => {
    if (!lmap.current || !window.L || !lat || !lng) return;
    const pos = [parseFloat(lat), parseFloat(lng)];
    if (marker.current) marker.current.setLatLng(pos);
    else marker.current = window.L.marker(pos).addTo(lmap.current);
    lmap.current.setView(pos, 16);
  }, [lat, lng]);

  return (
    <div style={{ marginTop:8, borderRadius:8, overflow:'hidden', border:'1px solid var(--border)' }}>
      <div style={{ padding:'6px 10px', background:'var(--bg)', fontSize:11, color:'var(--text-muted)' }}>
        🖱️ Spustelėkite ant žemėlapio norėdami pažymėti kameros vietą
      </div>
      <div ref={mapRef} style={{ width:'100%', height:300 }} />
    </div>
  );
}

export default function CameraManagement() {
  const [cameras, setCameras] = useState([]);
  const [clients, setClients] = useState([]);
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showMapPicker, setShowMapPicker] = useState(false);
  const [loadingScenarios, setLoadingScenarios] = useState(false);
  const [filterClient, setFilterClient] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    const [cams, cls] = await Promise.all([api.get('/cameras'), api.get('/clients')]);
    setCameras(cams.data);
    setClients(cls.data);
  }

  function openCreate() {
    setEditId(null);
    setForm(EMPTY_FORM);
    setModal(true);
  }

  function openEdit(cam) {
    setEditId(cam.id);
    setForm({ ...cam, password: '' });
    setModal(true);
  }

  async function handleSave(e) {
    e.preventDefault();
    setLoading(true);
    try {
      if (editId) {
        await api.put(`/cameras/${editId}`, form);
        toast.success('Kamera atnaujinta');
      } else {
        await api.post('/cameras', form);
        toast.success('Kamera pridėta');
      }
      setModal(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Klaida');
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete(id, name) {
    if (!window.confirm(`Ištrinti kamerą "${name}"?`)) return;
    await api.delete(`/cameras/${id}`);
    toast.success('Kamera ištrinta');
    loadData();
  }

  async function handleTest(cam) {
    setTesting(cam.id || 'new');
    try {
      const res = editId
        ? await api.post(`/cameras/${editId}/test`)
        : await api.post('/cameras/test-new', form);
      if (res.data.success) {
        toast.success('✅ Ryšys sėkmingas!');
      }
    } catch (err) {
      toast.error('❌ ' + (err.response?.data?.error || 'Nepavyko prisijungti'));
    } finally {
      setTesting(false);
    }
  }


  async function fetchScenarios() {
    if (!form.ip_address || !form.username) {
      toast.error('Įveskite IP adresą ir vartotojo vardą');
      return;
    }
    setLoadingScenarios(true);
    try {
      const res = editId
        ? await api.post(`/cameras/${editId}/get-scenarios`)
        : await api.post('/cameras/get-scenarios', form);
      const list = res.data.scenarios || [];
      setForm(f => ({...f, scenarios: JSON.stringify(list.map(s => ({id: s.id, name: s.name})))}));
      toast.success(`Rasta ${list.length} scenarijų`);
    } catch (err) {
      toast.error('Nepavyko nuskaityti: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoadingScenarios(false);
    }
  }

  const filtered = filterClient ? cameras.filter(c => String(c.client_id) === filterClient) : cameras;

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div className="page-title">Kamerų valdymas</div>
          <div className="page-subtitle">Pridėkite ir valdykite klientų kameras</div>
        </div>
        <button className="btn btn-primary" onClick={openCreate}>+ Pridėti kamerą</button>
      </div>

      <div className="filters-bar">
        <label>Filtruoti pagal klientą:</label>
        <select className="filter-input" value={filterClient} onChange={e => setFilterClient(e.target.value)}>
          <option value="">Visi klientai</option>
          {clients.map(c => <option key={c.id} value={c.id}>{c.company || c.name}</option>)}
        </select>
        <span style={{ marginLeft: 'auto', fontSize: 13, color: 'var(--text-muted)' }}>{filtered.length} kamera(-ų)</span>
      </div>

      <div className="card" style={{ padding: 0 }}>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Kamera</th>
                <th>IP adresas</th>
                <th>Tipas</th>
                <th>Klientas</th>
                <th>Paskutinis ryšys</th>
                <th>Būsena</th>
                <th>Veiksmai</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(cam => (
                <tr key={cam.id}>
                  <td>
                    <div style={{ fontWeight: 600 }}>{cam.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{cam.location}</div>
                  </td>
                  <td><code style={{ fontFamily: 'monospace', fontSize: 12 }}>{cam.ip_address}</code></td>
                  <td>
                    <span className={`badge ${cam.camera_type === 'both' ? 'badge-info' : cam.camera_type === 'people' ? 'badge-success' : 'badge-warning'}`}>
                      {cam.camera_type === 'both' ? '👥🚗 Mišrus' : cam.camera_type === 'people' ? '👥 Žmonės' : '🚗 Aut.'}
                    </span>
                  </td>
                  <td>{cam.client_name}</td>
                  <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    {cam.last_poll ? new Date(cam.last_poll).toLocaleString('lt-LT') : 'Niekada'}
                  </td>
                  <td>
                    <div className="cam-status">
                      <div className={`cam-dot ${cam.is_active ? 'online' : 'offline'}`} />
                      {cam.is_active ? 'Aktyvi' : 'Neaktyvi'}
                    </div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn btn-sm btn-secondary" onClick={() => openEdit(cam)}>✏️</button>
                      <button className="btn btn-sm btn-danger" onClick={() => handleDelete(cam.id, cam.name)}>🗑️</button>
                    </div>
                  </td>
                </tr>
              ))}
              {!filtered.length && (
                <tr><td colSpan={7} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: 40 }}>Kamerų nerasta</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <div className="modal-title">{editId ? 'Redaguoti kamerą' : 'Pridėti naują kamerą'}</div>
              <button className="modal-close" onClick={() => setModal(false)}>✕</button>
            </div>

            <form onSubmit={handleSave}>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Kameros pavadinimas *</label>
                  <input className="form-input" value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="Pagrindinis įėjimas" required />
                </div>
                <div className="form-group">
                  <label className="form-label">IP adresas *</label>
                  <input className="form-input" value={form.ip_address} onChange={e => setForm({...form, ip_address: e.target.value})} placeholder="192.168.1.100" required />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Vartotojo vardas *</label>
                  <input className="form-input" value={form.username} onChange={e => setForm({...form, username: e.target.value})} placeholder="admin" required />
                </div>
                <div className="form-group">
                  <label className="form-label">Slaptažodis {editId ? '(tuščia = nekeisti)' : '*'}</label>
                  <input className="form-input" type="password" value={form.password} onChange={e => setForm({...form, password: e.target.value})} placeholder="••••••••" required={!editId} />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Klientas *</label>
                  <select className="form-select" value={form.client_id} onChange={e => setForm({...form, client_id: e.target.value})} required>
                    <option value="">Pasirinkite klientą</option>
                    {clients.map(c => <option key={c.id} value={c.id}>{c.company || c.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Kameros tipas</label>
                  <select className="form-select" value={form.camera_type} onChange={e => setForm({...form, camera_type: e.target.value})}>
                    <option value="both">Mišrus (žmonės + auto)</option>
                    <option value="people">Tik žmonės</option>
                    <option value="vehicles">Tik automobiliai</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Vieta / aprašymas</label>
                  <input className="form-input" value={form.location} onChange={e => setForm({...form, location: e.target.value})} placeholder="A pastato įėjimas" />
                </div>
                <div className="form-group">
                  <label className="form-label">API endpoint (neprivalomas)</label>
                  <input className="form-input" value={form.api_endpoint} onChange={e => setForm({...form, api_endpoint: e.target.value})} placeholder="http://192.168.1.100/api/count" />
                </div>
              </div>

              {/* Koordinatės */}
              <div style={{ marginBottom:12 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:8 }}>
                  <label className="form-label" style={{ margin:0 }}>📍 Koordinatės žemėlapyje</label>
                  <button type="button" className="btn btn-sm btn-secondary" onClick={() => setShowMapPicker(v => !v)}>
                    {showMapPicker ? '▲ Slėpti žemėlapį' : '🗺️ Pasirinkti žemėlapyje'}
                  </button>
                </div>
                <div style={{ display:'flex', gap:8 }}>
                  <input className="form-input" type="number" step="any" value={form.lat||''} onChange={e => setForm({...form, lat: e.target.value})} placeholder="Platuma: 54.6872" />
                  <input className="form-input" type="number" step="any" value={form.lng||''} onChange={e => setForm({...form, lng: e.target.value})} placeholder="Ilguma: 25.2797" />
                  {form.lat && form.lng && (
                    <button type="button" className="btn btn-sm btn-danger" onClick={() => setForm({...form, lat:'', lng:''})} title="Išvalyti">✕</button>
                  )}
                </div>
                {showMapPicker && (
                  <MapPicker lat={form.lat} lng={form.lng} onPick={(lat, lng) => { setForm(f => ({...f, lat, lng})); }} />
                )}
                <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:4 }}>
                  💡 Arba Google Maps → dešiniu mygtuku → „Kas čia?" → nukopijuokite skaičius
                </div>
              </div>

              {editId && (
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                    <input type="checkbox" checked={form.is_active} onChange={e => setForm({...form, is_active: e.target.checked})} />
                    <span className="form-label" style={{ margin: 0 }}>Kamera aktyvi</span>
                  </label>
                </div>
              )}

              {/* Scenarijų konfigūracija */}
              <div className="form-group" style={{ marginTop:8 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
                  <label className="form-label" style={{ margin:0 }}>📋 Scenarijų sąrašas</label>
                  <button type="button" className="btn btn-sm btn-secondary"
                    onClick={fetchScenarios} disabled={loadingScenarios}
                    style={{ fontSize:11 }}>
                    {loadingScenarios ? <span className="loading-spinner" /> : '🔄'} Nuskaityti iš kameros
                  </button>
                </div>
                <div style={{ display:'flex', gap:6, marginBottom:4 }}>
                  <span style={{ fontSize:11, color:'var(--text-muted)', width:70, flexShrink:0 }}>Nr.</span>
                  <span style={{ fontSize:11, color:'var(--text-muted)' }}>Pavadinimas kameroje</span>
                </div>
                <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                  {(() => {
                    let scList = [{id:2, name:''}];
                    try {
                      const p = JSON.parse(form.scenarios || '[{"id":2,"name":""}]');
                      scList = p.map(s => typeof s === 'object' ? s : {id: s, name: ''});
                    } catch {}
                    return scList.map((sc, i) => (
                      <div key={i} style={{ display:'flex', gap:8, alignItems:'center' }}>
                        <input
                          className="form-input" type="number" min="1" max="99"
                          value={sc.id}
                          onChange={e => {
                            const list = [...scList];
                            list[i] = {...sc, id: parseInt(e.target.value) || 1};
                            setForm(f => ({...f, scenarios: JSON.stringify(list)}));
                          }}
                          style={{ width:70, flexShrink:0 }}
                          placeholder="Nr."
                        />
                        <input
                          className="form-input"
                          value={sc.name}
                          onChange={e => {
                            const list = [...scList];
                            list[i] = {...sc, name: e.target.value};
                            setForm(f => ({...f, scenarios: JSON.stringify(list)}));
                          }}
                          style={{ flex:1 }}
                          placeholder="pvz. Žmonės, Automobiliai, Dviračiai..."
                        />
                        {scList.length > 1 && (
                          <button type="button"
                            onClick={() => {
                              const list = scList.filter((_,j) => j !== i);
                              setForm(f => ({...f, scenarios: JSON.stringify(list)}));
                            }}
                            style={{ padding:'0 10px', background:'rgba(239,68,68,0.1)', border:'1px solid rgba(239,68,68,0.3)', borderRadius:6, color:'#ef4444', cursor:'pointer', flexShrink:0 }}>
                            ✕
                          </button>
                        )}
                      </div>
                    ));
                  })()}
                  <button type="button"
                    onClick={() => {
                      let list = [{id:2, name:''}];
                      try {
                        const p = JSON.parse(form.scenarios || '[{"id":2,"name":""}]');
                        list = p.map(s => typeof s === 'object' ? s : {id: s, name: ''});
                      } catch {}
                      setForm(f => ({...f, scenarios: JSON.stringify([...list, {id: list.length + 1, name: ''}])}));
                    }}
                    style={{ fontSize:12, color:'var(--accent)', background:'none', border:'none', cursor:'pointer', textAlign:'left', padding:'2px 0' }}>
                    ＋ Pridėti scenarijų
                  </button>
                </div>
                <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:4 }}>
                  Pavadinimas informaciniam tikslui. Duomenys iš visų scenarijų sumuojami į vieną ataskaitą.
                </div>
              </div>

              <div className="divider" />

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={() => handleTest(form)} disabled={testing !== false}>
                  {testing !== false ? <span className="loading-spinner" /> : '🔌'} Testuoti ryšį
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setModal(false)}>Atšaukti</button>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? <span className="loading-spinner" /> : editId ? 'Išsaugoti' : 'Pridėti'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
